/**
 * Camunda 7.18 REST API - Unified Service Facade
 * 
 * This provides a single entry point to all Camunda REST API services.
 * Based on: https://docs.camunda.org/manual/7.18/reference/rest/
 */

import { CamundaApiClient, getCamundaApiClient } from './CamundaApiClient';
import type { CamundaApiConfig } from './CamundaApiClient';

// Import all services
import { ProcessDefinitionService } from './services/ProcessDefinitionService';
import { ProcessInstanceService } from './services/ProcessInstanceService';
import { TaskService } from './services/TaskService';
import { DeploymentService } from './services/DeploymentService';
import { HistoryService } from './services/HistoryService';
import { ExternalTaskService } from './services/ExternalTaskService';
import { JobService } from './services/JobService';
import { IncidentService } from './services/IncidentService';
import { VariableService } from './services/VariableService';
import { UserGroupService } from './services/UserGroupService';
import { AuthorizationService } from './services/AuthorizationService';
import { MessageSignalService } from './services/MessageSignalService';
import { DecisionService } from './services/DecisionService';
import { BatchService } from './services/BatchService';
import { ExecutionService } from './services/ExecutionService';
import { FilterService } from './services/FilterService';
import { EngineService } from './services/EngineService';

/**
 * Unified Camunda Service providing access to all Camunda 7.18 REST APIs
 */
export class CamundaService {
  private client: CamundaApiClient;

  // Service instances
  public readonly processDefinition: ProcessDefinitionService;
  public readonly processInstance: ProcessInstanceService;
  public readonly task: TaskService;
  public readonly deployment: DeploymentService;
  public readonly history: HistoryService;
  public readonly externalTask: ExternalTaskService;
  public readonly job: JobService;
  public readonly incident: IncidentService;
  public readonly variable: VariableService;
  public readonly userGroup: UserGroupService;
  public readonly authorization: AuthorizationService;
  public readonly messageSignal: MessageSignalService;
  public readonly decision: DecisionService;
  public readonly batch: BatchService;
  public readonly execution: ExecutionService;
  public readonly filter: FilterService;
  public readonly engine: EngineService;

  constructor(config?: CamundaApiConfig) {
    this.client = config ? new CamundaApiClient(config) : getCamundaApiClient();

    // Initialize all services with the shared client
    this.processDefinition = new ProcessDefinitionService(this.client);
    this.processInstance = new ProcessInstanceService(this.client);
    this.task = new TaskService(this.client);
    this.deployment = new DeploymentService(this.client);
    this.history = new HistoryService(this.client);
    this.externalTask = new ExternalTaskService(this.client);
    this.job = new JobService(this.client);
    this.incident = new IncidentService(this.client);
    this.variable = new VariableService(this.client);
    this.userGroup = new UserGroupService(this.client);
    this.authorization = new AuthorizationService(this.client);
    this.messageSignal = new MessageSignalService(this.client);
    this.decision = new DecisionService(this.client);
    this.batch = new BatchService(this.client);
    this.execution = new ExecutionService(this.client);
    this.filter = new FilterService(this.client);
    this.engine = new EngineService(this.client);
  }

  /**
   * Get the underlying API client
   */
  getClient(): CamundaApiClient {
    return this.client;
  }

  // ============================================================================
  // Convenience Methods - Common Operations
  // ============================================================================

  /**
   * Deploy a BPMN process from XML
   */
  async deployProcess(options: {
    name: string;
    fileName: string;
    bpmnXml: string;
    tenantId?: string;
    deployChangedOnly?: boolean;
  }) {
    return this.deployment.createFromXml({
      deploymentName: options.name,
      fileName: options.fileName,
      bpmnXml: options.bpmnXml,
      tenantId: options.tenantId,
      deployChangedOnly: options.deployChangedOnly ?? true,
      deploymentSource: 'doit-apps',
    });
  }

  /**
   * Start a process instance by key
   */
  async startProcess(key: string, options?: {
    businessKey?: string;
    variables?: Record<string, any>;
    tenantId?: string;
  }) {
    const variables = options?.variables
      ? Object.fromEntries(
          Object.entries(options.variables).map(([k, v]) => [
            k,
            { value: v, type: typeof v === 'object' ? 'Json' : undefined },
          ])
        )
      : undefined;

    if (options?.tenantId) {
      return this.processDefinition.startByKeyAndTenant(key, options.tenantId, {
        businessKey: options?.businessKey,
        variables,
      });
    }

    return this.processDefinition.startByKey(key, {
      businessKey: options?.businessKey,
      variables,
    });
  }

  /**
   * Complete a user task
   */
  async completeTask(taskId: string, variables?: Record<string, any>) {
    const vars = variables
      ? Object.fromEntries(
          Object.entries(variables).map(([k, v]) => [
            k,
            { value: v, type: typeof v === 'object' ? 'Json' : undefined },
          ])
        )
      : undefined;

    return this.task.complete(taskId, { variables: vars });
  }

  /**
   * Claim and complete a task
   */
  async claimAndCompleteTask(taskId: string, userId: string, variables?: Record<string, any>) {
    await this.task.claim(taskId, userId);
    return this.completeTask(taskId, variables);
  }

  /**
   * Get tasks for a user
   */
  async getTasksForUser(userId: string, options?: {
    assignee?: boolean;
    candidateUser?: boolean;
    candidateGroup?: string;
    processDefinitionKey?: string;
    firstResult?: number;
    maxResults?: number;
  }) {
    return this.task.queryList({
      assignee: options?.assignee ? userId : undefined,
      candidateUser: options?.candidateUser ? userId : undefined,
      candidateGroup: options?.candidateGroup,
      processDefinitionKey: options?.processDefinitionKey,
      firstResult: options?.firstResult,
      maxResults: options?.maxResults ?? 50,
      sortBy: 'created',
      sortOrder: 'desc',
    });
  }

  /**
   * Get process instance with activity instances
   */
  async getProcessInstanceDetails(processInstanceId: string) {
    const [instance, activityInstance, variables] = await Promise.all([
      this.processInstance.getById(processInstanceId),
      this.processInstance.getActivityInstance(processInstanceId),
      this.processInstance.getVariables(processInstanceId),
    ]);

    return {
      instance,
      activityInstance,
      variables,
    };
  }

  /**
   * Correlate a message to a process
   */
  async correlateMessage(messageName: string, options?: {
    businessKey?: string;
    processInstanceId?: string;
    variables?: Record<string, any>;
    tenantId?: string;
  }) {
    const processVariables = options?.variables
      ? Object.fromEntries(
          Object.entries(options.variables).map(([k, v]) => [
            k,
            { value: v, type: typeof v === 'object' ? 'Json' : undefined },
          ])
        )
      : undefined;

    return this.messageSignal.correlateMessage({
      messageName,
      businessKey: options?.businessKey,
      processInstanceId: options?.processInstanceId,
      processVariables,
      tenantId: options?.tenantId,
      resultEnabled: true,
    });
  }

  /**
   * Get DoIT engine version
   */
  async getVersion() {
    return this.engine.getVersion();
  }

  /**
   * Health check - verify connection to DoIT engine
   */
  async healthCheck(): Promise<{ healthy: boolean; version?: string; error?: string }> {
    try {
      const version = await this.engine.getVersion();
      return { healthy: true, version: version.version };
    } catch (error) {
      return {
        healthy: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

// Singleton instance
let camundaServiceInstance: CamundaService | null = null;

export function getCamundaService(config?: CamundaApiConfig): CamundaService {
  if (!camundaServiceInstance) {
    camundaServiceInstance = new CamundaService(config);
  }
  return camundaServiceInstance;
}

export function resetCamundaService(): void {
  camundaServiceInstance = null;
}

export default CamundaService;
